import { Component, HostListener, OnDestroy, inject } from "@angular/core";
import { Router } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";

import { IcEnvironment } from "@intocare-medapp/environment";

import { AuthModel } from "../../shared/index";
import { SimpleToastService } from "../../shared/modules";
import { MFAFailedError } from "@intocare-medapp/aws/http/entities";
import { PermissionDeniedError } from "app/shared/errors/permission-denied.error";

@Component({
	selector: "ic-mfa-entry",
	templateUrl: "./mfa-entry.component.html",
	styleUrls: ["./mfa-entry.component.scss"],
	standalone: false,
})
export class MfaEntryComponent implements OnDestroy {
	private readonly authModel = inject(AuthModel);
	private readonly toastService = inject(SimpleToastService);
	private readonly translateService = inject(TranslateService);
	private readonly fb = inject(UntypedFormBuilder);
	private readonly router = inject(Router);
	private readonly icEnvironment = inject(IcEnvironment);

	private readonly MFA_INACTIVITY_TIMEOUT = 2 * 60 * 1000; // 2 minutes

	form: UntypedFormGroup = this.fb.group({
		token: ["", Validators.required],
	});
	appVersion = this.icEnvironment.getEnvironment().APP_VERSION;
	// @ts-expect-error TS2564
	userActivity: ReturnType<typeof setTimeout>;
	userInactive = new Subject<boolean | undefined>();
	userInactiveSubscription: Subscription;

	constructor() {
		this.setInactivityTimeout();
		// @ts-expect-error TS2769
		this.userInactiveSubscription = this.userInactive.subscribe((next: boolean) => {
			if (next) {
				console.info(`MFA entry, user inactive for ${this.MFA_INACTIVITY_TIMEOUT / (60 * 1000)} minutes`);
				this.form.reset();
				this.router.navigate(["auth/sign-in-with-username"], { queryParams: { redirectedByInactivity: true } });
			}
		});
	}

	ngOnDestroy(): void {
		this.clearInactivityTimeout();
		this.userInactiveSubscription.unsubscribe();
	}

	clearInactivityTimeout() {
		if (this.userActivity) {
			// clear any existing timeout
			clearTimeout(this.userActivity);
		}
	}

	setInactivityTimeout() {
		this.clearInactivityTimeout();
		// start the timer over again 2 minutes
		this.userActivity = setTimeout(() => this.userInactive.next(true), this.MFA_INACTIVITY_TIMEOUT);
	}

	sendToken({ token }: { token: number }) {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const token_padded = token.toString().padStart(6, "0");

		this.authModel.sendMfaToken(token_padded).subscribe({
			next: () => {
				this.router.navigate(["user-briefing"]);
			},
			error: (err) => {
				this.authModel.hideLoadingDialog();

				let message = "auth.errors.unknown";

				if (err instanceof MFAFailedError) {
					message = "auth.errors.mfa_failed";
				}

				if (err && err.code === "NetworkError") {
					message = "general.warnings.network_lost";
				}

				if (err instanceof PermissionDeniedError) {
					message = "auth.errors.permission_denied";
				}

				if (!(err instanceof MFAFailedError)) {
					this.cancel();
				}
				this.toastService.error(this.translateService.instant(message));
			},
		});
	}

	cancel() {
		this.router.navigate(["select-organisation"]);
	}

	get hasTokenError() {
		const control = this.form.get("token");

		// @ts-expect-error TS18047
		return control.touched && control.invalid;
	}

	@HostListener("window:mousemove") refreshUserState() {
		this.setInactivityTimeout();
	}

	@HostListener("window:keydown") refreshUserState2() {
		this.setInactivityTimeout();
	}

	@HostListener("window:scroll") refreshUserState3() {
		this.setInactivityTimeout();
	}

	@HostListener("window:click") refreshUserState4() {
		this.setInactivityTimeout();
	}

	@HostListener("touchstart") refreshUserState5() {
		this.setInactivityTimeout();
	}

	@HostListener("touchmove") refreshUserState6() {
		this.setInactivityTimeout();
	}
}
